/**
 * Font Download Service
 * Downloads font file bytes from backend and caches them in IndexedDB
 */

import axios from 'axios';
import type { FontData } from '../../core/types/Typesetter';

const DB_NAME = 'edgedocs-fonts';
const DB_VERSION = 1;
const STORE_NAME = 'fonts';

/**
 * Get authentication token from Keycloak instance or fallback to storage
 */
function getAuthToken(): string | null {
  // Try Keycloak instance first (primary method)
  const keycloak = (window as any).keycloak;
  if (keycloak?.token) {
    return keycloak.token;
  }

  // Fallback to localStorage/sessionStorage
  return (
    localStorage.getItem('webelieve_token') ||
    sessionStorage.getItem('webelieve_token') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token') ||
    localStorage.getItem('webelieve_access_token')
  );
}

interface FontCacheEntry {
  font_uid: string;
  bytes: Uint8Array;
  timestamp: number;
  size: number;
}

class FontDownloadServiceClass {
  private db: IDBDatabase | null = null;
  private dbPromise: Promise<IDBDatabase> | null = null;
  private memoryCache: Map<string, Uint8Array> = new Map();
  private lruOrder: string[] = [];
  private MAX_CACHE_BYTES = 32 * 1024 * 1024; // 32MB cap
  private totalBytesCached = 0;

  /**
   * Normalize a font UID by stripping any file extension so that
   * cache keys and API identifiers align across callers.
   */
  private normalizeUidNoExt(font_uid: string): string {
    return font_uid.replace(/\.(ttf|otf|woff2?|TTF|OTF|WOFF2?)$/, '');
  }

  /**
   * Initialize IndexedDB
   */
  private async initDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'font_uid' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('[FontDownloadService] Created IndexedDB object store');
        }
      };
    });

    return this.dbPromise;
  }

  /**
   * Get font from IndexedDB cache
   */
  private async getCachedFont(font_uid: string): Promise<Uint8Array | null> {
    const cacheKey = this.normalizeUidNoExt(font_uid);
    // Check memory cache first (fastest)
    if (this.memoryCache.has(cacheKey)) {
      console.log(`[FontDownloadService] Memory cache HIT for ${cacheKey}`);
      return this.memoryCache.get(cacheKey)!;
    }

    // Check IndexedDB cache
    try {
      const db = await this.initDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(cacheKey);

      return new Promise((resolve) => {
        request.onsuccess = () => {
          const entry: FontCacheEntry | undefined = request.result;
          if (entry) {
            console.log(`[FontDownloadService] IndexedDB cache HIT for ${cacheKey} (${(entry.size / 1024).toFixed(1)}KB)`);
            // Add to memory cache for faster subsequent access
            this.memoryCache.set(cacheKey, entry.bytes);
            resolve(entry.bytes);
          } else {
            console.log(`[FontDownloadService] Cache MISS for ${cacheKey}`);
            resolve(null);
          }
        };
        request.onerror = () => {
          console.error(`[FontDownloadService] IndexedDB error:`, request.error);
          resolve(null);
        };
      });
    } catch (error) {
      console.error('[FontDownloadService] Failed to access IndexedDB:', error);
      return null;
    }
  }

  /**
   * Store font in IndexedDB cache
   */
  private async cacheFont(font_uid: string, bytes: Uint8Array): Promise<void> {
    const cacheKey = this.normalizeUidNoExt(font_uid);
    // Store in memory cache
    this.memoryCache.set(cacheKey, bytes);
    this.touchLru(cacheKey, bytes.byteLength);

    // Store in IndexedDB for persistence
    try {
      const db = await this.initDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      const entry: FontCacheEntry = {
        font_uid: cacheKey,
        bytes,
        timestamp: Date.now(),
        size: bytes.byteLength,
      };

      store.put(entry);

      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });

      console.log(`[FontDownloadService] Cached ${font_uid} (${(bytes.byteLength / 1024).toFixed(1)}KB)`);
      // Enforce LRU cap after storing
      await this.enforceLruCap();
    } catch (error) {
      console.error('[FontDownloadService] Failed to cache font:', error);
      // Continue without caching (memory cache still works)
    }
  }

  private touchLru(key: string, size: number): void {
    // Move key to end (most recently used)
    this.lruOrder = this.lruOrder.filter(k => k !== key);
    this.lruOrder.push(key);
    this.totalBytesCached += size;
  }

  private async enforceLruCap(): Promise<void> {
    if (this.totalBytesCached <= this.MAX_CACHE_BYTES) return;
    try {
      const db = await this.initDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      while (this.totalBytesCached > this.MAX_CACHE_BYTES && this.lruOrder.length > 0) {
        const victim = this.lruOrder.shift()!;
        // Remove from memory
        const victimBytes = this.memoryCache.get(victim);
        if (victimBytes) this.totalBytesCached -= victimBytes.byteLength;
        this.memoryCache.delete(victim);
        // Remove from IndexedDB
        store.delete(victim);
        console.log(`[FontDownloadService] LRU evicted: ${victim}`);
      }

      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (err) {
      console.warn('[FontDownloadService] LRU enforcement failed:', err);
    }
  }

  /**
   * Validate font bytes to ensure they're actually a font file
   */
  private validateFontBytes(bytes: Uint8Array, font_uid: string): boolean {
    // Check minimum size (font files should be at least a few KB)
    if (bytes.length < 100) {
      console.error(`[FontValidation] ❌ Font too small: ${font_uid} (${bytes.length} bytes)`);
      return false;
    }

    // Check file signature (first 4 bytes identify font format)
    const signature = String.fromCharCode(...bytes.slice(0, 4));
    const validSignatures = [
      'OTTO',           // OpenType with CFF
      '\x00\x01\x00\x00', // TrueType 1.0
      'true',           // TrueType (old Mac)
      'typ1',           // PostScript Type 1
      'ttcf',           // TrueType Collection
      'wOFF',           // WOFF (Web Open Font Format)
      'wOF2'            // WOFF2 (Web Open Font Format 2.0)
    ];

    if (!validSignatures.includes(signature)) {
      const hexSig = Array.from(bytes.slice(0, 4))
        .map(b => b.toString(16).padStart(2, '0'))
        .join(' ');
      console.error(`[FontValidation] ❌ Invalid font signature for ${font_uid}: "${signature}" (hex: ${hexSig})`);
      console.error(`[FontValidation] Expected one of: ${validSignatures.join(', ')}`);
      return false;
    }

    console.log(`[FontValidation] ✅ Valid font signature for ${font_uid}: "${signature}"`);
    return true;
  }

  /**
   * Download font bytes from backend
   */
  async downloadFont(font_uid: string, fileId?: string): Promise<Uint8Array> {
    const normalizedUid = this.normalizeUidNoExt(font_uid);
    // Check cache first
    const cached = await this.getCachedFont(normalizedUid);
    if (cached) {
      return cached;
    }

    // Download from backend using canonical file id WITH extension
    const urlId = (fileId && fileId.length > 0) ? fileId : font_uid;
    const url = `/api/v1/edgedocs/fonts/${encodeURIComponent(urlId)}`;

    console.log(`[FontDownloadService] Downloading ${font_uid} from ${url}...`);

    try {
      // Get authentication token
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await axios.get(url, {
        responseType: 'arraybuffer',
        timeout: 30000, // 30 second timeout
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const bytes = new Uint8Array(response.data);
      console.log(`[FontDownloadService] Downloaded ${urlId} (${(bytes.byteLength / 1024).toFixed(1)}KB)`);

      // ✅ NEW: Validate font bytes before caching
      if (!this.validateFontBytes(bytes, normalizedUid)) {
        throw new Error(`Downloaded file is not a valid font: ${urlId}`);
      }

      // Cache for future use
      await this.cacheFont(normalizedUid, bytes);

      return bytes;
    } catch (error: any) {
      console.error(`[FontDownloadService] ❌ Failed to download ${urlId}:`, error.message);
      throw new Error(`Failed to download font ${urlId}: ${error.message}`);
    }
  }

  /**
   * Preload multiple fonts (parallel download)
   */
  async preloadFonts(fontSpecs: Array<{ font_uid: string; fileId?: string }>): Promise<FontData[]> {
    console.log(`[FontDownloadService] Preloading ${fontSpecs.length} fonts:`, fontSpecs.map(s => s.font_uid));

    const downloadPromises = fontSpecs.map(async (spec) => {
      try {
        console.log(`[FontDownloadService] Downloading ${spec.font_uid}...`);
        const bytes = await this.downloadFont(spec.font_uid, spec.fileId);
        console.log(`[FontDownloadService] ✅ Downloaded ${spec.font_uid} (${bytes.length} bytes)`);
        return { font_uid: spec.font_uid, bytes };
      } catch (error) {
        console.error(`[FontDownloadService] ❌ Failed to preload ${spec.font_uid}:`, error);
        return null;
      }
    });

    const results = await Promise.all(downloadPromises);
    const fonts = results.filter((f): f is FontData => f !== null);

    console.log(`[FontDownloadService] Preloaded ${fonts.length}/${fontSpecs.length} fonts successfully:`, fonts.map(f => `${f.font_uid} (${f.bytes.length} bytes)`));

    return fonts;
  }

  /**
   * Clear all cached fonts
   */
  async clearCache(): Promise<void> {
    this.memoryCache.clear();

    try {
      const db = await this.initDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.clear();

      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });

      console.log('[FontDownloadService] Cache cleared');
    } catch (error) {
      console.error('[FontDownloadService] Failed to clear cache:', error);
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<{ count: number; totalSize: number }> {
    try {
      const db = await this.initDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();

      return new Promise((resolve) => {
        request.onsuccess = () => {
          const entries: FontCacheEntry[] = request.result || [];
          const totalSize = entries.reduce((sum, e) => sum + e.size, 0);
          resolve({ count: entries.length, totalSize });
        };
        request.onerror = () => resolve({ count: 0, totalSize: 0 });
      });
    } catch (error) {
      return { count: 0, totalSize: 0 };
    }
  }
}

// Export singleton instance
export const FontDownloadService = new FontDownloadServiceClass();

